import { useState } from 'react';
import { X, Check } from 'lucide-react';

interface Category {
  id: string;
  group_name: string;
  name: string;
  label: string;
  description: string | null;
  color: string | null;
}

interface AnnotationPopoverProps {
  categories: Record<string, Category[]>;
  selectedCategories: string[];
  onSelect: (categoryIds: string[]) => void;
  onConfirm: () => void;
  onCancel: () => void;
  position: { x: number; y: number };
}

const GROUP_LABELS: Record<string, string> = {
  confidence: 'Confidence Signals',
  source: 'Source & Evidence',
  structure: 'Structure & Rhetoric',
  issues: 'Potential Issues'
};

export function AnnotationPopover({
  categories,
  selectedCategories,
  onSelect,
  onConfirm,
  onCancel,
  position
}: AnnotationPopoverProps) {
  const [selected, setSelected] = useState<string[]>(selectedCategories);

  const toggleCategory = (id: string) => {
    const newSelected = selected.includes(id)
      ? selected.filter(s => s !== id)
      : [...selected, id];
    setSelected(newSelected);
    onSelect(newSelected);
  };

  return (
    <div
      className="fixed z-50 bg-gray-900 border border-gray-700 rounded-lg shadow-xl p-3 w-80"
      style={{ left: position.x, top: position.y }}
    >
      <div className="flex justify-between items-center mb-3">
        <span className="text-sm font-medium text-gray-300">Tag this passage</span>
        <button onClick={onCancel} className="text-gray-500 hover:text-gray-300">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="space-y-3 max-h-64 overflow-y-auto">
        {Object.entries(categories).map(([group, cats]) => (
          <div key={group}>
            <div className="text-xs font-semibold text-gray-500 uppercase mb-1">
              {GROUP_LABELS[group] || group}
            </div>
            <div className="flex flex-wrap gap-1">
              {cats.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => toggleCategory(cat.id)}
                  className={`text-xs px-2 py-1 rounded-full border transition-colors ${
                    selected.includes(cat.id)
                      ? 'border-transparent'
                      : 'border-gray-600 bg-gray-800 text-gray-400 hover:border-gray-500'
                  }`}
                  style={selected.includes(cat.id) ? {
                    backgroundColor: `${cat.color}30`,
                    color: cat.color || undefined,
                    borderColor: cat.color || undefined
                  } : undefined}
                  title={cat.description || undefined}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="flex justify-end gap-2 mt-3 pt-3 border-t border-gray-700">
        <button
          onClick={onCancel}
          className="px-3 py-1.5 text-sm text-gray-400 hover:text-gray-200"
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          disabled={selected.length === 0}
          className="flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed rounded"
        >
          <Check className="w-3 h-3" />
          Add Tag{selected.length > 1 ? 's' : ''}
        </button>
      </div>
    </div>
  );
}

export default AnnotationPopover;
